import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { HttpErrorResponse } from '@angular/common/http'
import { CallerService } from '../services/caller.service'
import { AccommodationActionsService } from '../services/accommodation-actions.service'
import { AccommodationType } from '../ngrx-store/app.state'
import { SeoService } from '../services/seo.service'
import { RETURN_URL_QUERY_PARAM } from '../utils/return-url.util'
import { leavingLabel, saleLocation } from '../utils/sale.util'
import { accommodationStatusLabel, accommodationTypeLabel, rentIntervalLabel } from '../utils/accommodation.util'

/** One house (/a/:id) - a shared link, so logged out too. Laid out like the house card, with everything on it. */
@Component({
  selector: 'app-public-accommodation',
  templateUrl: './public-accommodation.component.html',
  styleUrls: ['./public-accommodation.component.css'],
})
export class PublicAccommodationComponent implements OnInit, OnDestroy {
  accommodation?: AccommodationType
  isLoading = true
  notFound = false
  loadError = false

  /** The corp member's saved houses (logged in only) - whichever arrives first, this or the house. */
  private savedIds = new Set<number>()
  isSaving = false

  private destroy$ = new Subject<void>()

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public callerService: CallerService,
    public houseActions: AccommodationActionsService,
    private seo: SeoService,
  ) {}

  ngOnInit() {
    this.loadAccommodation()
    if (this.callerService.isLoggedIn) {
      this.houseActions.savedIds().pipe(takeUntil(this.destroy$)).subscribe((ids) => (this.savedIds = ids))
    }
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadAccommodation() {
    this.isLoading = true
    this.notFound = false
    this.loadError = false

    const id = this.route.snapshot.paramMap.get('id')
    this.callerService.getPublicAccommodation(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.body?.data) {
          this.accommodation = res.body.data
          this.updateSeo(res.body.data)
        } else {
          this.showNotFound()
        }
        this.isLoading = false
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.showNotFound()
        } else {
          this.loadError = true
        }
        this.isLoading = false
      },
    })
  }

  get saved(): boolean {
    return !!this.accommodation && this.savedIds.has(this.accommodation.id)
  }

  toggleSave() {
    if (!this.accommodation) return
    this.isSaving = true
    // The store's saved ids update, and with them `saved`
    this.houseActions.toggleSave(this.accommodation.id, this.saved).subscribe(() => (this.isSaving = false))
  }

  get isOwn(): boolean {
    return this.callerService.isLoggedIn && !!this.accommodation?.corp_member_id &&
      this.accommodation.corp_member_id === this.callerService.corpMember?.id
  }

  get typeLabel(): string {
    return accommodationTypeLabel(this.accommodation?.accommodation_type)
  }

  /** "Ikeja, Lagos". */
  get location(): string {
    return saleLocation(this.accommodation?.CorpMember?._location)
  }

  get isFree(): boolean {
    return this.accommodation?.rent_interval === 'free'
  }

  get rentIntervalLabel(): string {
    return rentIntervalLabel(this.accommodation?.rent_interval)
  }

  get statusLabel(): string | undefined {
    return accommodationStatusLabel(this.accommodation?.status)
  }

  get seekingRoommate(): boolean {
    return this.accommodation?.status === 'seeking_roommate'
  }

  /** The facts about the place, shown as a grid - only the ones this listing has. */
  get facts(): Array<{ icon: string; label: string; value: string; date?: boolean }> {
    const a = this.accommodation
    if (!a) return []
    return [
      this.statusLabel && { icon: 'info', label: 'Status', value: this.statusLabel },
      a.available_rooms?.length && { icon: 'bed', label: 'Rooms', value: a.available_rooms.join(', ') },
      a.rent_expire_date && { icon: 'event', label: 'Available from', value: a.rent_expire_date, date: true },
      a.Location?.address && { icon: 'pin_drop', label: 'Address', value: a.Location.address },
    ].filter((fact): fact is { icon: string; label: string; value: string; date?: boolean } => !!fact)
  }

  get posterName(): string {
    return this.accommodation?.CorpMember?.nickname ?? this.accommodation?.CorpMember?.first_name ?? 'The poster'
  }

  get posterInitial(): string {
    return (this.accommodation?.CorpMember?.first_name?.[0] ?? 'P').toUpperCase()
  }

  get posterProfileLink(): string[] {
    const slug = this.accommodation?.CorpMember?.slug_id ?? this.accommodation?.CorpMember?.created_at
    return slug ? ['/p', slug] : ['/home']
  }

  get leaving(): string | undefined {
    return leavingLabel(this.accommodation?.CorpMember?.service_year_end_date)
  }

  /** Logged out: sign up, then come back to this house. */
  get signupQueryParams(): Record<string, string> {
    return { [RETURN_URL_QUERY_PARAM]: this.router.url.split(/[?#]/)[0] }
  }

  private showNotFound() {
    this.notFound = true
    this.seo.setPage({ title: 'Not found', path: this.router.url, noindex: true })
  }

  private updateSeo(accommodation: AccommodationType) {
    const where = saleLocation(accommodation.CorpMember?._location) || accommodation.CorpMember?.service_state
    const type = accommodation.accommodation_type ? accommodationTypeLabel(accommodation.accommodation_type) : ''
    const rent = this.isFree ? 'Free' : accommodation._rent ? `₦${accommodation._rent}${this.rentIntervalLabel}` : ''
    // "corpers lodge" is what people search for (Search Console), not "accommodation".
    const lodge = `${type ? 'corpers lodge' : 'Corpers lodge'}${where ? ` in ${where}` : ''}`
    this.seo.setPage({
      title: [type, lodge, rent].filter(Boolean).join(' – '),
      description: [accommodation.occupant_description, `Posted by a corps member on corpers.ng.`]
        .filter(Boolean)
        .join(' · '),
      image: accommodation.Media?.[0]?.url,
      path: this.router.url,
    })
  }
}
